import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TAXONOMY_AGENT_ID } from '@inference.engine/taxonomy/web';
import { assetsApiAgent, usePageAgentPanel, type PageAgentChat } from '@/entities/agent';
import { TAXONOMY_TOOL_LABEL_KEYS, useToolLabels } from './tool-labels';
import { useTaxonomyAgentExtensions } from './useTaxonomyAgentExtensions';
import { useAgentProgress, useFileKickoff, useOpenedFile } from './useFileKickoff';

const NO_IDS: ReadonlySet<string> = new Set();
/** Long enough to see which rows the agent changed, short enough not to linger. */
const HIGHLIGHT_MS = 2400;

const PROMPT_KEYS = [
  'taxonomy.agent.prompts.addRows',
  'taxonomy.agent.prompts.addColumn',
  'taxonomy.agent.prompts.rename',
] as const;

/**
 * What a taxonomy page needs to host its agent: the panel, the agent's chat, whose extensions
 * carry its writes onto the page, and the rows to highlight after a write. A page opened to fill
 * the taxonomy from a file opens the panel and hands the agent that file.
 */
export function useTaxonomyAgentHost(datasetId: string) {
  const { t } = useTranslation('knowledge');

  const dirtyIds = useRef<ReadonlySet<string>>(NO_IDS);
  const onDirtyIdsChange = useCallback((ids: ReadonlySet<string>) => {
    dirtyIds.current = ids;
  }, []);
  const [highlightedIds, setHighlightedIds] = useState<ReadonlySet<string>>(NO_IDS);
  const highlightTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(highlightTimer.current), []);
  const taxonomyExtensions = useTaxonomyAgentExtensions({
    datasetId,
    getDirtyIds: () => dirtyIds.current,
    onChanged: (ids) => {
      setHighlightedIds(new Set(ids));
      clearTimeout(highlightTimer.current);
      highlightTimer.current = setTimeout(() => setHighlightedIds(NO_IDS), HIGHLIGHT_MS);
    },
  });
  const progress = useAgentProgress();
  const extensions = useMemo(
    () => [...taxonomyExtensions, progress.extension],
    [taxonomyExtensions, progress.extension],
  );

  const opened = useOpenedFile(datasetId);
  const kickoff = useFileKickoff(
    TAXONOMY_AGENT_ID,
    opened,
    t('taxonomy.agent.fromFile', { fileName: opened?.importFile.fileName ?? '' }),
  );

  const toolLabels = useToolLabels(TAXONOMY_TOOL_LABEL_KEYS);
  const chat = useMemo<PageAgentChat>(
    () => ({
      chatId: datasetId,
      relay: assetsApiAgent(TAXONOMY_AGENT_ID, t('taxonomy.agent.name'), toolLabels),
      extensions,
      acceptsFiles: true,
      intro: {
        title: t('taxonomy.agent.name'),
        description: t('taxonomy.agent.empty'),
        prompts: PROMPT_KEYS.map((key) => t(key)),
      },
      ...(kickoff ? { initialMessage: kickoff } : {}),
      ...(progress.busyLabel ? { busyLabel: progress.busyLabel } : {}),
      onTurnEnd: progress.onTurnEnd,
    }),
    [t, datasetId, toolLabels, extensions, kickoff, progress.busyLabel, progress.onTurnEnd],
  );

  const panel = usePageAgentPanel(TAXONOMY_AGENT_ID, Boolean(opened));
  return { panel, chat, highlightedIds, onDirtyIdsChange };
}
